import { Controller } from '@hotwired/stimulus'

const GAP = 8 // px between the toggle and the floated menu

// Floats the sidebar account dropdowns (org switcher, user menu) to the RIGHT of
// their toggle — like the React/Mantine sidebar.
//
// Bootstrap disables Popper for dropdowns inside a .navbar (so they expand inline
// / static), so we position the menu ourselves as a `position: fixed` popover on
// show. Simple menus (the user menu) are also portaled to <body> while open, so
// they always paint above page content instead of being trapped in / clipped by
// the sidebar's stacking context. The org switcher menu has its own controller
// that must stay inside the .dropdown, so it is NOT portaled (and works fine at
// the top of the sidebar over the empty header area).
//
// direction value: "" → opens downward (org, at top); "up" → opens upward (user,
// pinned at the bottom).
export default class extends Controller {
  static values = { direction: String }

  connect() {
    this.menu = this.element.parentElement?.querySelector('.dropdown-menu')
    // Don't portal a menu that has its own controller (the org switcher), whether
    // on the menu element itself or nested — it relies on staying inside .dropdown.
    this.portalable =
      !!this.menu && !this.menu.hasAttribute('data-controller') && !this.menu.querySelector('[data-controller]')
    this.onShow = this.position.bind(this)
    this.onHidden = this.returnHome.bind(this)
    this.element.addEventListener('show.bs.dropdown', this.onShow)
    this.element.addEventListener('shown.bs.dropdown', this.onShow)
    this.element.addEventListener('hidden.bs.dropdown', this.onHidden)
  }

  disconnect() {
    this.element.removeEventListener('show.bs.dropdown', this.onShow)
    this.element.removeEventListener('shown.bs.dropdown', this.onShow)
    this.element.removeEventListener('hidden.bs.dropdown', this.onHidden)
    this.returnHome()
  }

  position() {
    if (!this.menu) return

    if (this.portalable && this.menu.parentElement !== document.body) {
      this.home = this.element.parentElement
      document.body.appendChild(this.menu)
    }

    const rect = this.element.getBoundingClientRect()
    const { style } = this.menu

    style.position = 'fixed'
    style.left = `${rect.right + GAP}px` // always to the right of the toggle
    style.right = 'auto'
    style.margin = '0'
    style.transform = 'none'
    style.zIndex = '1056'

    if (this.directionValue === 'up') {
      // Bottom-aligned: menu's bottom edge meets the toggle's bottom, opens upward.
      style.top = 'auto'
      style.bottom = `${window.innerHeight - rect.bottom}px`
    } else {
      // Top-aligned, opens downward; clamp so a tall menu never runs off-screen.
      style.bottom = 'auto'
      const height = this.menu.offsetHeight
      const maxTop = window.innerHeight - height - GAP
      style.top = `${height ? Math.min(rect.top, Math.max(GAP, maxTop)) : rect.top}px`
    }
  }

  // Move a portaled menu back next to its toggle so Bootstrap can find it again.
  returnHome() {
    if (this.home && this.menu && this.menu.parentElement === document.body) {
      this.home.appendChild(this.menu)
    }
  }
}
